import { Input } from "@/components/inputs";
import { Card, Container, CriterioContainer } from "./styles";
import { Page } from "@/components/page-base";
import { DeleteOutline } from "@mui/icons-material";
import { ScenarioCardsDisplay } from "../scenario-cards-display";
import {
  TParticipation,
  TScenario,
  TScenarioFormState,
  TScenarioItem
} from "../../models";
import permissionsStore from "../../../../store/permissions";
import { useEffect, useState } from "react";
import { ScenariosService } from "../../services";
import { toast } from "react-toastify";
import { ModalDelete } from "@/components/modal-template/modal-delete";
import { useNavigationContext } from "@/contexts";
import { removeFromLocalStorage } from "@/modules/consult/utils/utils";
import {
  setDefaultScenario,
  updateScenarioId
} from "@/utils/setDefaultScenario";
import { TCriterioData } from "@/modules/users/models";

interface IScenarioItemProps {
  scenarios: TScenario[];
  selectedAccountId: number;
  fetchData: () => Promise<void>;
}
type TCriterioProps = {
  criterioId: number;
  cardId: number;
};
export const ScenarioItem: React.FC<IScenarioItemProps> = ({
  scenarios,
  selectedAccountId,
  fetchData
}) => {
  const defaultScenariosForm: TScenarioFormState[] = [
    {
      id: 1,
      label: "Considerar esfera Criminal na reprovação",
      isEnabled: false,
      participation: { ativo: false, reu: false, citado: false }
    },
    {
      id: 2,
      label: "Considerar esfera Trabalhista na reprovação",
      isEnabled: false,
      participation: { ativo: false, reu: false, citado: false }
    },
    {
      id: 3,
      label: "Considerar esfera Cível na reprovação",
      isEnabled: false,
      participation: { ativo: false, reu: false, citado: false }
    },
    {
      id: 4,
      label: "Considerar esfera Eleitoral na reprovação",
      isEnabled: false,
      participation: { ativo: false, reu: false, citado: false }
    }
  ];
  const { role, scenarioId, setScenarioId, storageUser } = permissionsStore();
  const { setDefaultCriterioId } = useNavigationContext();
  const [scenarioItem, setScerarioItem] = useState<TScenarioItem[]>([]);
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [changedCriterioProps, setChangedCriterioProps] =
    useState<TCriterioProps>({ criterioId: 0, cardId: 0 });
  const toggleModalDelete = () => setModalDelete((prevState) => !prevState);

  const normalizeScenarioId = (scenarioId: TCriterioData[]) => {
    if (!Array.isArray(scenarioId)) {
      // Converte o número para um array com um objeto único
      return [{ scenarioId, accountId: selectedAccountId }];
    }
    return scenarioId;
  };

  const getCriterioId = (
    scenarioId: TCriterioData[],
    selectedAccountId: number
  ) => {
    const normalizedScenario = normalizeScenarioId(scenarioId);
    return normalizedScenario.find(
      (scenario) => scenario.accountId === selectedAccountId
    )?.scenarioId;
  };

  useEffect(() => {
    setScerarioItem((prev) => {
      const formatedScenario: TScenarioItem[] = scenarios.map((s, idx) => {
        return {
          accountId: s.accountId || 0,
          id: s.id,
          name: s.name,
          prompt: s.prompt
            ? parsePrompt(s.prompt, prev[idx]?.prompt)
            : defaultScenariosForm
        };
      });
      return formatedScenario;
    });
    // eslint-disable-next-line
  }, [scenarios]);

  const checkDisableCriterio = (scenario: TScenarioItem) => {
    const participation = scenario.prompt.every(({ participation }) =>
      Object.values(participation).every((value) => value === false)
    );
    const allDisabled = scenario.prompt.every(
      ({ isEnabled }) => isEnabled === false
    );

    return participation && allDisabled;
  };
  const checkEnableCriterio = (scenario: TScenarioItem) => {
    const someParticipation = scenario.prompt.some(({ participation }) =>
      Object.values(participation).some((value) => value === true)
    );
    const someEnabled = scenario.prompt.some(
      ({ isEnabled }) => isEnabled === true
    );
    return someParticipation && someEnabled;
  };

  useEffect(() => {
    const changedCriterio = scenarioItem.find(
      (item) => item.id == changedCriterioProps.criterioId
    );
    if (changedCriterio) {
      let criterio = deparsePrompt(changedCriterio.prompt);
      if (!criterio) {
        criterio = "";
      }
      if (canUpdate(changedCriterio.prompt, changedCriterioProps.cardId)) {
        editCriterio(
          changedCriterio.name,
          criterio,
          changedCriterioProps.criterioId
        );
      } else {
        editCriterio(
          changedCriterio.name,
          removeInvalidLines(criterio),
          changedCriterioProps.criterioId
        );
      }
      if (
        checkDisableCriterio(changedCriterio) &&
        changedCriterio.id === getCriterioId(scenarioId, selectedAccountId)
      ) {
        disableCriterio();
      } else if (
        checkEnableCriterio(changedCriterio) &&
        changedCriterio.id === getCriterioId(scenarioId, selectedAccountId)
      ) {
        enableCriterio(changedCriterio);
      }
    }
    // eslint-disable-next-line
  }, [scenarioItem]);

  const removeInvalidLines = (input: string): string => {
    return input
      .split("\n")
      .filter((line) => !/;;;;/.test(line))
      .join("\n");
  };

  const canUpdate = (
    scenarios: TScenarioFormState[],
    cardId: number
  ): boolean => {
    const updatedCriterioCard = scenarios.findIndex((c) => c.id == cardId);
    if (!scenarios[updatedCriterioCard].isEnabled) {
      return true;
    } else {
      const { ativo, citado, reu } =
        scenarios[updatedCriterioCard].participation;
      return ativo || citado || reu;
    }
  };

  const updateScenario = (
    cardId: number,
    updatedFields: Partial<TScenarioFormState>,
    scenarioId: number
  ) => {
    const updatedValues: TScenarioItem[] = [];
    setChangedCriterioProps({ criterioId: scenarioId, cardId: cardId });
    scenarioItem.forEach((scenario) => {
      if (scenario.id === scenarioId) {
        const updatedPrompt = scenario.prompt.map((p) =>
          p.id === cardId ? { ...p, ...updatedFields } : p
        );
        updatedValues.push({ ...scenario, prompt: updatedPrompt });
      } else {
        updatedValues.push(scenario);
      }
    });
    setScerarioItem(updatedValues);
  };

  const handleToggleParticipation = (
    cardId: number,
    field: keyof TParticipation,
    scenarioId: number
  ) => {
    setChangedCriterioProps({ criterioId: scenarioId, cardId: cardId });
    const updatedValues: TScenarioItem[] = [];
    scenarioItem.forEach((scenario) => {
      if (scenario.id === scenarioId) {
        const updatedPrompt = scenario.prompt.map((p) =>
          p.id === cardId
            ? {
                ...p,
                participation: {
                  ...p.participation,
                  [field]: !p.participation[field]
                }
              }
            : p
        );
        updatedValues.push({ ...scenario, prompt: updatedPrompt });
      } else {
        updatedValues.push(scenario);
      }
    });
    setScerarioItem(updatedValues);
  };
  const handleToggleEnabled = (cardId: number, scenarioId: number) => {
    setChangedCriterioProps({ criterioId: scenarioId, cardId: cardId });
    const currentScenario = scenarioItem.find((s) => s.id === scenarioId);
    const scenario = currentScenario?.prompt.find((s) => s.id === cardId);
    if (scenario) {
      const newEnabled = !scenario.isEnabled;
      const updatedFields: Partial<TScenarioFormState> = {
        isEnabled: newEnabled
      };
      if (!newEnabled) {
        updatedFields.participation = {
          ativo: false,
          reu: false,
          citado: false
        };
      }
      updateScenario(cardId, updatedFields, scenarioId);
    }
  };

  const parsePrompt = (
    prompt: string,
    prevState?: TScenarioFormState[]
  ): TScenarioFormState[] => {
    const esferaIndices: Record<string, number> = {
      Criminal: 0,
      Trabalhista: 1,
      Cível: 2,
      Eleitoral: 3
    };

    const mapedEsferas = prompt
      .split(/\r?\n/)
      .map((esfera) => esfera.split(";"));
    const updatedCriteria = prevState
      ? [...prevState]
      : [...defaultScenariosForm];
    mapedEsferas.forEach(([tipo, ativo, reu, citado]) => {
      const index = esferaIndices[tipo];
      updatedCriteria[index] = {
        ...updatedCriteria[index],
        isEnabled: true,
        participation: {
          ativo: ativo === "ATIVO",
          reu: reu === "PASSIVO",
          citado: citado === "CITADO"
        }
      };
    });

    return updatedCriteria;
  };
  const deparsePrompt = (prompt: TScenarioFormState[]) => {
    const esferaIndices: Record<number, string> = {
      0: "Criminal",
      1: "Trabalhista",
      2: "Cível",
      3: "Eleitoral"
    };

    const updatedPrompt = prompt
      .map((scenario, index) => {
        const { isEnabled, participation } = scenario;
        if (!isEnabled) return "";
        const promptString = `${esferaIndices[index]};${
          participation.ativo ? "ATIVO" : ""
        };${participation.reu ? "PASSIVO" : ""};${
          participation.citado ? "CITADO" : ""
        }`;
        return promptString.endsWith(";;;;true") ? "" : promptString;
      })
      .filter(Boolean)
      .join("\n")
      .trim();

    return updatedPrompt.replace(/^\s*[\r\n]/gm, "");
  };

  const sanitizePrompt = (input: string): string => {
    const lines = input.split(/\r?\n/);
    const filteredLines = lines.filter((line) => line.slice(-3) !== ";;;");
    return filteredLines.join("\n");
  };

  const editCriterio = async (
    titlePrompt: string,
    prompt: string,
    criterioId: number
  ) => {
    try {
      const response = await ScenariosService.editScenario(
        titlePrompt,
        sanitizePrompt(prompt),
        selectedAccountId,
        criterioId
      );
      if (response?.success) {
        console.debug("Success");
      } else {
        toast.error(response?.message || "Erro ao atualizar");
      }
    } catch (error) {
      toast.error("Ocorreu um erro ao atualizar o critério.");
      console.error("Erro ao atualizar critério:", error);
    }
  };
  const deleteScenario = async (accountId: number, scenarioId: number) => {
    try {
      const response = await ScenariosService.deleteScenario(
        accountId,
        scenarioId
      );
      if (response?.success) {
        toast.success(response.message);
        const hidePosition = scenarioItem.findIndex((i) => i.id == scenarioId);
        if (hidePosition !== -1) {
          const updatedScenarios = [...scenarioItem];
          updatedScenarios.splice(hidePosition, 1);
          setScerarioItem(updatedScenarios);
        }
        fetchData();
        toggleModalDelete();
      } else {
        toast.error(response?.message || "Erro ao excluir o critério.");
      }
    } catch (error) {
      toast.error("Ocorreu um erro ao excluir o critério.");
      console.error("Erro ao excluir critério:", error);
    }
  };
  const handleDelete = (id: number) => {
    setChangedCriterioProps({ criterioId: id, cardId: 0 });
    toggleModalDelete();
  };

  const disableCriterio = async () => {
    const index = scenarios.findIndex((s) => s.prompt === null);
    if (index !== -1) {
      const result = await setDefaultScenario(
        selectedAccountId,
        scenarios[index].id,
        storageUser!.id
      );
      if (result.success) {
        setScenarioId(
          updateScenarioId(selectedAccountId, scenarioId, scenarios[index].id)
        );
      } else {
        toast.error("Ocorreu um erro ao alterar o critério.");
      }
      setDefaultCriterioId(0);
      setDefaultCriterioId(scenarios[index].id);
    } else {
      try {
        const response = await ScenariosService.createScenario(
          "Critério Padrão",
          null,
          selectedAccountId
        );
        if (response.success) {
          toast.success(response.message);
          setScenarioId(
            updateScenarioId(
              selectedAccountId,
              scenarioId,
              response.scenarioId!
            )
          );
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        toast.error("Ocorreu um erro ao desabilitar critério.");
        console.error("Erro ao desabilitar critério", error);
      }
    }
  };
  const enableCriterio = async (changedCriterio: TScenarioItem) => {
    const result = await setDefaultScenario(
      selectedAccountId,
      changedCriterio.id,
      storageUser!.id
    );
    if (result.success) {
      setScenarioId(
        updateScenarioId(selectedAccountId, scenarioId, changedCriterio.id)
      );
    } else {
      toast.error("Ocorreu um erro ao alterar o critério.");
    }
    setDefaultCriterioId(0); //Caso o último critério atualizado, seja o atual a ser ativado, forço um comportamento para atualizar a lista de critérios.
    setDefaultCriterioId(changedCriterio.id);
  };
  return (
    <>
      <ModalDelete
        isOpen={modalDelete}
        title="Deseja excluir o critério?"
        action={() =>
          deleteScenario(selectedAccountId, changedCriterioProps.criterioId)
        }
        description="Após a exclusão o critério não poderá mais ser usado."
        toggle={toggleModalDelete}
        isDisabledButton={false}
      />
      <Container>
        <CriterioContainer>
          {scenarioItem
            .filter(
              (item) => item.prompt !== null && item.name !== "Critério Padrão"
            )
            .slice()
            .sort((a, b) =>
              a.id === getCriterioId(scenarioId, selectedAccountId)
                ? -1
                : b.id === getCriterioId(scenarioId, selectedAccountId)
                ? 1
                : 0
            )
            .map((_) => (
              <Card
                isActive={_.id === getCriterioId(scenarioId, selectedAccountId)}
                key={_.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <div className="top-container">
                  <div className="left-container">
                    <span className="label">{_.name}</span>
                  </div>
                  <div className="right-container">
                    <span className="right-label">
                      {_.id === getCriterioId(scenarioId, selectedAccountId)
                        ? "Em uso"
                        : "Desativado"}{" "}
                    </span>
                    <Input.Toggle
                      checked={
                        _.id === getCriterioId(scenarioId, selectedAccountId)
                      }
                      onChange={async () => {
                        removeFromLocalStorage("analysisResult");
                        if (
                          _.id === getCriterioId(scenarioId, selectedAccountId)
                        ) {
                          disableCriterio();
                        } else {
                          const result = await setDefaultScenario(
                            selectedAccountId,
                            _.id,
                            storageUser!.id
                          );
                          if (result.success) {
                            setScenarioId(
                              updateScenarioId(
                                selectedAccountId,
                                scenarioId,
                                _.id
                              )
                            );
                          } else {
                            toast.error(
                              "Ocorreu um erro ao alterar o critério."
                            );
                          }
                        }
                      }}
                      disabled={false}
                      backgroundColor={
                        _.id === getCriterioId(scenarioId, selectedAccountId)
                          ? "#3A7D44"
                          : "#A0A0A0"
                      }
                    />
                  </div>
                </div>
                <div className="card-content">
                  <ScenarioCardsDisplay
                    onToggleEnabled={handleToggleEnabled}
                    onToggleParticipation={handleToggleParticipation}
                    scenariosForm={_.prompt}
                    readOnly={role === "collaborator"}
                    scenarioId={_.id}
                  />
                </div>
                {role === "owner" && (
                  <div className="bottom-container">
                    <div className="right-container">
                      <Page.Button
                        label=""
                        startIcon={<DeleteOutline />}
                        onClick={() => {
                          if (
                            _.id ===
                            getCriterioId(scenarioId, selectedAccountId)
                          ) {
                            toast.warn("Critério em uso!");
                          } else {
                            handleDelete(_.id);
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </Card>
            ))}
        </CriterioContainer>
      </Container>
    </>
  );
};
